/**
 * Durable Flex flags written when a moderation removal settles.
 * Inbox, dashboard, and the booking page hide Join from these — not
 * from the in-memory session (that is lost on deploy).
 */

const GUIDELINES_END_REASONS = new Set([
  'policy_severe',
  'contact_policy',
  'sexual_policy',
  'conduct_policy',
]);

const protectedDataOf = tx => tx?.attributes?.protectedData || {};

/**
 * Talk session that ended for a Community Guidelines removal.
 *
 * @param {Object} [tx]
 * @returns {{ endedReason: string, removedRole: string|null, chargeOutcome: string|null }|null}
 */
export const transactionGuidelinesRemoval = tx => {
  const data = protectedDataOf(tx);
  const endedReason = data.endedReason;
  const isRemoval = data.contactRemoval === true || GUIDELINES_END_REASONS.has(endedReason);
  if (!isRemoval) {
    return null;
  }
  return {
    endedReason: GUIDELINES_END_REASONS.has(endedReason) ? endedReason : 'policy_severe',
    removedRole: data.contactRemovedRole || null,
    chargeOutcome: data.chargeOutcome || null,
  };
};

/**
 * Hide Join / Upcoming once a removal flag is on the booking.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const transactionHidesJoin = tx => !!transactionGuidelinesRemoval(tx);

/**
 * Charged / released copy on the booking page. Only nudity (or sexual
 * content involving a minor) auto-ends and can charge — those are
 * `policy_severe`. Contact / harassment / adult sexual talk never show
 * “charged in full”, including pre-v461 flags that used those reasons.
 *
 * @param {Object} [tx]
 * @returns {'charged'|'released'|null}
 */
export const transactionGuidelinesMoney = tx => {
  const removal = transactionGuidelinesRemoval(tx);
  if (!removal || removal.endedReason !== 'policy_severe') {
    return null;
  }
  if (removal.chargeOutcome === 'release_hold' || removal.removedRole === 'listener') {
    return 'released';
  }
  if (removal.chargeOutcome === 'charge_talker' || removal.removedRole === 'talker') {
    return 'charged';
  }
  return null;
};

const CHARGED_OUTCOMES = new Set(['charge_talker', 'charged']);
const NOT_CHARGED_OUTCOMES = new Set(['release_hold', 'not_charged']);

const bookingEndMs = tx => {
  const end = tx?.booking?.attributes?.end;
  const ms = end instanceof Date ? end.getTime() : Date.parse(end);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Talker money result for the booking page price box once the session is
 * removed or over. null = still upcoming (show the pending / cancel copy).
 *
 * @param {Object} [tx]
 * @param {number} [nowMs]
 * @returns {'charged'|'not_charged'|'ended'|null}
 */
export const transactionTalkerChargeResult = (tx, nowMs = Date.now()) => {
  const data = protectedDataOf(tx);
  const removal = transactionGuidelinesRemoval(tx);
  if (removal) {
    if (
      NOT_CHARGED_OUTCOMES.has(removal.chargeOutcome) ||
      (!removal.chargeOutcome && removal.removedRole === 'listener')
    ) {
      return 'not_charged';
    }
    if (
      CHARGED_OUTCOMES.has(removal.chargeOutcome) ||
      (!removal.chargeOutcome && removal.removedRole === 'talker')
    ) {
      return 'charged';
    }
    return 'ended';
  }
  if (CHARGED_OUTCOMES.has(data.chargeOutcome)) {
    return 'charged';
  }
  if (NOT_CHARGED_OUTCOMES.has(data.chargeOutcome)) {
    return 'not_charged';
  }
  const endMs = bookingEndMs(tx);
  if (endMs != null && nowMs > endMs) {
    return 'ended';
  }
  return null;
};

export { GUIDELINES_END_REASONS };
